import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../lib/api'

/**
 * Hook for rotating contextual suggestions in the input placeholder
 * 
 * Features:
 * - Fetches suggestions from backend based on league/context
 * - Rotates through suggestions every 7s with fade transition
 * - Freezes on hover/focus/typing
 * - Resumes rotation when idle
 * - Filters expired suggestions
 */
export function useRotatingSuggestion({
  league = 'nba',
  context = 'home', // 'home' | 'pulse'
  pinnedGameId = null,
  enabled = true,
}) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFrozen, setIsFrozen] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const timerRef = useRef(null)
  
  // Fetch suggestions from backend
  const { data, refetch } = useQuery({
    queryKey: ['suggestions', league, context, pinnedGameId],
    queryFn: () => apiClient.getSuggestions(league, context, pinnedGameId),
    enabled,
    staleTime: 30 * 1000, // 30s
    refetchInterval: 60 * 1000, // Refetch every minute
  })
  
  const suggestions = data?.suggestions || []
  
  // Filter out expired suggestions
  const activeSuggestions = suggestions.filter(s => {
    if (!s.expiresAt) return true
    return s.expiresAt > Date.now()
  })
  
  // Current suggestion
  const currentSuggestion = activeSuggestions[currentIndex % Math.max(activeSuggestions.length, 1)] || null
  
  // Rotate to next suggestion
  const rotateNext = useCallback(() => {
    if (activeSuggestions.length <= 1) return
    
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex(prev => (prev + 1) % activeSuggestions.length)
      setIsTransitioning(false)
    }, 150) // Fade duration
  }, [activeSuggestions.length])
  
  // Start/stop rotation timer
  useEffect(() => {
    if (isFrozen || !enabled || activeSuggestions.length <= 1) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }
    
    timerRef.current = setInterval(rotateNext, 7000) // 7s dwell
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [isFrozen, enabled, rotateNext, activeSuggestions.length])
  
  // Reset index when suggestions change significantly
  useEffect(() => {
    if (currentIndex >= activeSuggestions.length) {
      setCurrentIndex(0)
    }
  }, [activeSuggestions.length, currentIndex])
  
  // Freeze controls
  const freeze = useCallback(() => setIsFrozen(true), [])
  const unfreeze = useCallback(() => setIsFrozen(false), [])
  
  // Manual navigation
  const goToNext = useCallback(() => {
    if (activeSuggestions.length > 1) {
      setCurrentIndex(prev => (prev + 1) % activeSuggestions.length)
    }
  }, [activeSuggestions.length])
  
  const goToPrev = useCallback(() => {
    if (activeSuggestions.length > 1) {
      setCurrentIndex(prev => (prev - 1 + activeSuggestions.length) % activeSuggestions.length)
    }
  }, [activeSuggestions.length])
  
  return {
    currentSuggestion,
    suggestions: activeSuggestions,
    currentIndex,
    isTransitioning,
    isFrozen,
    freeze,
    unfreeze,
    goToNext,
    goToPrev,
    refetch,
    hasMultiple: activeSuggestions.length > 1,
  }
}

export default useRotatingSuggestion
